// IE 2026 09 26 / ODOO-… (заказ Игоря: виды панели страниц, п. 1 — «зачем прокрутка вниз, если можно показать в несколько колонок»): расчёт раскладки
// панелей-меню (components/MenuPanel.vue) по ЗАМЕРЕННЫМ высотам групп — чистые функции (юнит-тест рядом с патчем).
//   · menuPanelFit_partition — разбиение последовательности групп на n колонок подряд (порядок групп сохраняется, группа целиком в одной колонке)
//     с наименьшей высотой самой высокой колонки (то же правило, что MenuPanel._layout, но по пикселям);
//   · menuPanelFit_columns — сколько колонок взять: прежнее число (n0 = min(maxCols, групп)), если панель с ним помещается по высоте окна; иначе
//     наименьшее большее, при котором помещается, пока хватает ширины окна (nMax); не помещается ни при каком — наименьшее число колонок с самой
//     низкой панелью (самая высокая группа ограничивает — лишние колонки панель не расширяют);
//   · menuPanelFit_plan — то же с делением ВЫСОКИХ групп (группа сама выше доступной высоты колонки): такая группа режется по границам блоков
//     (страница вместе с её виджетами — страница никогда не отрывается от своих виджетов), в следующей колонке её заголовок повторяется с пометкой
//     продолжения; группы, которые помещаются, остаются целиком; число колонок — наименьшее, при котором панель помещается, не помещается ни при каком —
//     наименьшее с самой низкой панелью (обычно по ширине окна). Решение координатора 26.09: «не прокручивать, если можно показать колонками»;
//   · menuPanelCards_fit — вид «карточки» (превью страниц): наибольшая ширина карточки из диапазона 240…150 px, при которой секции групп (подпись группы
//     слева, карточки рядами справа) помещаются по высоте; не помещается ни при какой — наименьшая ширина (150 px: мельче схема не читается — дальше прокрутка).

// heights — высоты групп в px (с зазором после группы), n — число колонок → { maxH, cols: [[индексы групп]] }
export function menuPanelFit_partition(heights, n) {
    const m = heights.length
    if (!m) return { maxH: 0, cols: [] }
    n = Math.max(1, Math.min(n, m))
    const pre = [0]
    for (const x of heights) pre.push(pre[pre.length - 1] + (Number(x) || 0))
    const INF = 1e15
    const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(INF))
    const cut = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0))
    dp[0][0] = 0
    for (let k = 1; k <= n; k++) {
        for (let i = 1; i <= m; i++) {
            for (let j = k - 1; j < i; j++) {
                const v = Math.max(dp[k - 1][j], pre[i] - pre[j])
                if (v < dp[k][i]) { dp[k][i] = v; cut[k][i] = j }
            }
        }
    }
    const cols = []
    let i = m
    for (let k = n; k >= 1; k--) { const j = cut[k][i]; const col = []; for (let x = j; x < i; x++) col.push(x); cols.unshift(col); i = j }
    return { maxH: dp[n][m], cols: cols.filter(c => c.length) }
}

// { heights (px, группа + её нижний зазор gapV), n0, nMax (колонок по ширине окна), chrome (всё, кроме колонок: заголовок, поиск, поля), avail (высота
// под панель), gapV } → { n, colH, fits, changed }; colH — высота самой высокой колонки (без последнего зазора)
export function menuPanelFit_columns({ heights, n0, nMax, chrome = 0, avail = Infinity, gapV = 0 }) {
    const m = (heights || []).length
    const base = Math.max(1, Math.min(n0 || 1, m || 1))
    if (m <= 1) return { n: base, colH: m ? heights[0] - gapV : 0, fits: !m || heights[0] - gapV + chrome <= avail, changed: false }
    const colH = (n) => menuPanelFit_partition(heights, n).maxH - gapV
    const h0 = colH(base)
    if (h0 + chrome <= avail) return { n: base, colH: h0, fits: true, changed: false }
    let best = base, bestH = h0
    const top = Math.max(base, Math.min(m, nMax || base))
    for (let n = base + 1; n <= top; n++) {
        const h = colH(n)
        if (h + chrome <= avail) return { n, colH: h, fits: true, changed: true }
        if (h < bestH - 0.5) { best = n; bestH = h }
    }
    return { n: best, colH: bestH, fits: false, changed: best !== base }
}

// группы → раскладка с делением высоких групп. groups: [{ key, h (px, с нижним зазором gapV), hdr (px: заголовок группы до первого пункта), blocks: [{ h, items:
// [ключи пунктов] }] }] (блок — пункт верхнего уровня с подчинёнными: страница + её виджеты). → { n, colH, fits, changed, split, cols } — cols: колонки из
// элементов { key } (группа целиком) либо { key, part, cont, items } (кусок группы: part — номер куска, cont — продолжение, items — ключи его пунктов);
// cols = null — раскладка прежняя (не менялась)
export function menuPanelFit_plan({ groups, n0, nMax, chrome = 0, avail = Infinity, gapV = 0 }) {
    const list = groups || []
    const m = list.length
    const base = Math.max(1, Math.min(n0 || 1, m || 1))
    const hs = list.map(g => Number(g.h) || 0)
    const colAvail = avail - chrome
    const big = list.map((g, i) => hs[i] - gapV > colAvail + 0.5 && Array.isArray(g.blocks) && g.blocks.length > 1)
    if (!big.some(Boolean)) {
        const r = menuPanelFit_columns({ heights: hs, n0, nMax, chrome, avail, gapV })
        return { ...r, split: false, cols: r.changed ? menuPanelFit_partition(hs, r.n).cols.map(c => c.map(i => ({ key: String(list[i].key) }))) : null }
    }
    // единицы раскладки: целая группа либо блок высокой группы
    const units = []
    list.forEach((g, gi) => { if (!big[gi]) units.push({ gi, h: hs[gi] }); else g.blocks.forEach((b, bi) => units.push({ gi, bi, h: Number(b.h) || 0 })) })
    const U = units.length
    // высота колонки из единиц j..i-1: целая группа — её высота с зазором; кусок высокой группы — заголовок (у продолжения — повтор с пометкой) + блоки + зазор;
    // минус зазор после последней группы колонки
    const cost = Array.from({ length: U + 1 }, () => new Array(U + 1).fill(0))
    for (let j = 0; j < U; j++) {
        let c = 0, prev = -1
        for (let i = j; i < U; i++) {
            const u = units[i]
            if (u.bi === undefined) { c += u.h; prev = -1 }
            else { if (u.gi !== prev) c += (Number(list[u.gi].hdr) || 0) + gapV; c += u.h; prev = u.gi }
            cost[j][i + 1] = c - gapV
        }
    }
    const solve = (n) => {
        n = Math.max(1, Math.min(n, U))
        const INF = 1e15
        const dp = Array.from({ length: n + 1 }, () => new Array(U + 1).fill(INF))
        const cut = Array.from({ length: n + 1 }, () => new Array(U + 1).fill(0))
        dp[0][0] = 0
        for (let k = 1; k <= n; k++) for (let i = 1; i <= U; i++) for (let j = k - 1; j < i; j++) { const v = Math.max(dp[k - 1][j], cost[j][i]); if (v < dp[k][i]) { dp[k][i] = v; cut[k][i] = j } }
        const ranges = []
        let i = U
        for (let k = n; k >= 1; k--) { const j = cut[k][i]; ranges.unshift([j, i]); i = j }
        return { maxH: dp[n][U], ranges: ranges.filter(([a, b]) => b > a) }
    }
    const top = Math.max(base, Math.min(U, nMax || base))
    let best = null
    for (let n = base; n <= top; n++) {
        const r = solve(n)
        if (r.maxH + chrome <= avail) { best = { r, fits: true }; break }
        if (!best || r.maxH < best.r.maxH - 0.5) best = { r, fits: false }
    }
    const parts = new Map()
    const cols = best.r.ranges.map(([a, b]) => {
        const out = []
        let cur = null
        for (let i = a; i < b; i++) {
            const u = units[i], g = list[u.gi]
            if (u.bi === undefined) { out.push({ key: String(g.key) }); cur = null; continue }
            if (!cur || cur.gi !== u.gi) {
                const part = parts.get(u.gi) || 0
                parts.set(u.gi, part + 1)
                cur = { gi: u.gi, e: { key: String(g.key), part, cont: u.bi > 0, items: [] } }
                out.push(cur.e)
            }
            cur.e.items.push(...(g.blocks[u.bi].items || []).map(String))
        }
        return out
    })
    return { n: cols.length, colH: best.r.maxH, fits: best.fits, changed: true, split: true, cols }
}

// колонок по ширине окна: панель = n колонок + зазоры + поля + рамка; ширина окна минус 16 px (place: панель не шире окна − 16)
export function menuPanelFit_colsByWidth({ vw, colWidth, colGap, pad = 18, border = 2 }) {
    return Math.max(1, Math.floor((vw - 16 - 2 * pad - border + colGap) / (colWidth + colGap)))
}

// вид «карточки»: counts — число карточек в секциях групп (по порядку, пустые пропускаются); availW / availH — ширина рядов карточек и высота под
// секции; ratio — пропорция схемы (ширина / высота рабочей области страницы); cap — высота подписи и рамки карточки; gap — зазор между карточками
// и секциями; wMax…wMin с шагом step → { w, perRow, cardH, h, rows, fits }
export function menuPanelCards_fit({ counts, availW, availH, ratio = 2, wMax = 240, wMin = 150, step = 10, gap = 8, cap = 22 }) {
    const list = (counts || []).filter(c => c > 0)
    const calc = (w) => {
        const perRow = Math.max(1, Math.floor((availW + gap) / (w + gap)))
        const cardH = cap + Math.round((w - 2) / Math.max(0.5, ratio))
        let rows = 0
        for (const c of list) rows += Math.ceil(c / perRow)
        const h = rows ? rows * cardH + (rows - 1) * gap : 0
        return { w, perRow, cardH, h, rows }
    }
    let last = null
    for (let w = wMax; w >= wMin; w -= step) {
        const r = calc(w)
        if (r.h <= availH) return { ...r, fits: true }
        last = r
    }
    return { ...(last || calc(wMin)), fits: false }
}

// IE 2026 10 13 / ODOO-345046 (заказ Игоря 13.10, скрин DM «Сторінки» → превью: одна колонка плиток с прокруткой при одной группе): плитки
// колонками «как список» (fe.ux.pagesTilesColumns) — только когда групп страниц больше одной; одна группа (групп нет) — плитки рядами
// по ширине окна (menuPanelCards_fit подбирает размер, чтобы всё легло без прокрутки)
export function menuPanelCards_useCols(colsFlagOn, groupsCount) {
    return !!colsFlagOn && (Number(groupsCount) || 0) > 1
}

export default { menuPanelFit_partition, menuPanelFit_columns, menuPanelFit_plan, menuPanelFit_colsByWidth, menuPanelCards_fit, menuPanelCards_useCols }
